<template>
  <div class="node-settings">
    <!-- Trigger Node Settings -->
    <template v-if="type === 'trigger'">
      <SelectInput
        :id="`trigger-type`"
        :label="'Trigger type'"
        v-model="localData.type"
        :options="triggerTypes"
        :disabled="hasConnections"
        @update:modelValue="handleTypeChange"
      />

      <template v-if="localData.type === 'form_submission'">
        <template v-if="forms.length">
          <SelectInput
            id="form-select"
            label="Select funnel"
            v-model="localData.config.formId"
            :options="formOptions"
            @update:modelValue="emitUpdate"
          />
        </template>
        <template v-else>
          <div class="alert alert--info">
            No funnels found. First add a funnel at the
            <router-link :to="{ path: '/all-flows' }">funnels page</router-link>
            .
          </div>
        </template>
      </template>

      <template v-if="localData.type === 'status_change'">
        <SelectInput
          id="from-status"
          label="From status"
          v-model="localData.config.fromStatus"
          :options="dealStatuses"
          @update:modelValue="emitUpdate"
        />
        <SelectInput
          id="to-status"
          label="To status"
          v-model="localData.config.toStatus"
          :options="dealStatuses.filter((status) => status.value !== 'new')"
        />
      </template>

      <template v-if="localData.type === 'deal_created'">
        <SelectInput
          id="deal-created-pipeline"
          label="Pipeline"
          v-model="localData.config.pipelineId"
          :options="pipelineOptions"
          placeholder="Alle pipelines"
          @update:modelValue="emitUpdate"
        />
        <SelectInput
          id="deal-created-source"
          label="Bron"
          v-model="localData.config.source"
          :options="[
            { value: 'any', label: 'Elke bron' },
            { value: 'manual', label: 'Handmatig' },
            { value: 'proposal', label: 'Vanuit proposal' },
            { value: 'configurator', label: 'Configurator' },
            { value: 'import', label: 'Import' },
          ]"
          @update:modelValue="emitUpdate"
        />
      </template>

      <template v-if="localData.type === 'deal_status_change'">
        <SelectInput
          id="deal-pipeline"
          label="Pipeline"
          v-model="localData.config.pipelineId"
          :options="pipelineOptions"
          placeholder="All pipelines"
          @update:modelValue="handlePipelineChange"
        />
        <SelectInput
          id="deal-status-type"
          label="Status type"
          v-model="localData.config.statusType"
          :options="dealStatusTypeOptions"
          placeholder="All status changes"
          @update:modelValue="emitUpdate"
        />
        <template v-if="!localData.config.statusType || localData.config.statusType === 'any'">
          <SelectInput
            id="deal-from-stage"
            label="From stage"
            v-model="localData.config.fromStageId"
            :options="stageOptions"
            placeholder="Any stage"
            @update:modelValue="emitUpdate"
          />
          <SelectInput
            id="deal-to-stage"
            label="To stage"
            v-model="localData.config.toStageId"
            :options="stageOptions"
            placeholder="Any stage"
            @update:modelValue="emitUpdate"
          />
        </template>
      </template>

      <template
        v-if="
          localData.type === 'proposal_accepted' ||
          localData.type === 'proposal_rejected'
        "
      >
        <SelectInput
          id="proposal-type"
          label="Offerte-type"
          v-model="localData.config.proposalType"
          :options="proposalTypeOptions"
          @update:modelValue="emitUpdate"
        />
        <div class="node-settings__info-tooltip">
          <BaseIcon name="info-circle" size="13" class="node-settings__info-icon" />
          <span>
            Kies <code>Alle types</code> om voor elke offerte te vuren, of beperk de
            automation tot één offerte-type. Werkt ook voor offertes zonder gekoppelde
            deal.
          </span>
        </div>
      </template>

      <template v-if="localData.type === 'proposal_status_change'">
        <SelectInput
          id="proposal-from-status"
          label="Van status"
          v-model="localData.config.fromStatus"
          :options="proposalStatusOptions"
          @update:modelValue="emitUpdate"
        />
        <SelectInput
          id="proposal-to-status"
          label="Naar status"
          v-model="localData.config.toStatus"
          :options="proposalStatusOptions"
          @update:modelValue="emitUpdate"
        />
        <SelectInput
          id="proposal-type-status-change"
          label="Offerte-type"
          v-model="localData.config.proposalType"
          :options="proposalTypeOptions"
          @update:modelValue="emitUpdate"
        />
      </template>

      <template
        v-if="
          localData.type === 'project_status_change' ||
          localData.type === 'project_fully_completed'
        "
      >
        <template v-if="localData.type === 'project_status_change'">
          <SelectInput
            id="project-from-status"
            label="Van status"
            v-model="localData.config.fromStatus"
            :options="projectStatusOptions"
            @update:modelValue="emitUpdate"
          />
          <SelectInput
            id="project-to-status"
            label="Naar status"
            v-model="localData.config.toStatus"
            :options="projectStatusOptions"
            @update:modelValue="emitUpdate"
          />
        </template>
        <div class="node-settings__info-tooltip">
          <BaseIcon name="info-circle" size="13" class="node-settings__info-icon" />
          <span v-if="localData.type === 'project_fully_completed'">
            Vuurt wanneer een project de status <code>completed</code> krijgt.
          </span>
          <span v-else>
            Kies <code>Elke status</code> om op alle overgangen te vuren. Voor
            "project volledig afgerond" kies je naar-status <code>Afgerond</code>.
          </span>
        </div>
      </template>

      <template v-if="WORK_ORDER_TRIGGER_TYPES.includes(localData.type)">
        <SelectInput
          id="work-order-type"
          label="Werkbon-type"
          v-model="localData.config.workOrderType"
          :options="workOrderTypeOptions"
          @update:modelValue="emitUpdate"
        />
        <div class="node-settings__info-tooltip">
          <BaseIcon name="info-circle" size="13" class="node-settings__info-icon" />
          <span v-if="localData.type === 'work_visit_confirmed'">
            Vuurt één keer per bezoekcluster bij bevestiging, niet per dag.
          </span>
          <span v-else-if="localData.type === 'work_visit_completed'">
            Vuurt als de laatste werkbon van een bezoekcluster klaar is, terwijl
            het project nog andere bezoeken heeft.
          </span>
          <span v-else-if="localData.type === 'project_all_work_orders_completed'">
            Vuurt als alle werkbonnen op het project klaar zijn. Gebruik dit voor
            review/NPS in Loops.
          </span>
          <span v-else>
            Vuurt wanneer een werkbon wordt afgerond (met of zonder uitzonderingen).
          </span>
        </div>
      </template>

      <template v-if="localData.type === 'scheduled_event'">
        <SelectInput
          id="frequency"
          label="Frequency"
          v-model="localData.config.frequency"
          :options="[
            { value: 'daily', label: 'Daily' },
            { value: 'weekly', label: 'Weekly' },
            { value: 'monthly', label: 'Monthly' },
          ]"
        />

        <!-- Schedule settings based on frequency -->
        <ScheduleSettings
          v-model="localData.config.schedule_settings"
          :frequency="localData.config.frequency"
        />
      </template>
    </template>

    <!-- Condition Node Settings (top-level condition node) -->
    <template v-else-if="type === 'condition'">
      <ConditionConfig
        :model-value="localData.config"
        :currentAutomation="currentAutomation"
        @update:model-value="handleConfigUpdate"
      />
    </template>

    <!-- Action Node Settings -->
    <template v-else-if="type === 'action'">
      <template v-if="localData.type === 'integration'">
        <template v-if="availableIntegrations.length">
          <SelectInput
            id="integration-select"
            label="Select integration"
            :options="availableIntegrations.map((i) => ({ label: i.name, value: i.id }))"
            v-model="localData.config.integrationId"
            @update:modelValue="emitUpdate"
          />
          <div
            v-if="localData.config.integrationId"
            class="node-settings__integration-info"
          >
            <p class="node-settings__integration-desc">
              {{ selectedIntegrationDescription }}
            </p>
            <router-link
              :to="{ path: '/settings/integrations' }"
              class="node-settings__integration-link"
              target="_blank"
            >
              Integratie-instellingen beheren →
            </router-link>
          </div>

              <!-- ActiveCampaign: tag + contact fields -->
          <template v-if="selectedIntegration?.type === 'activecampaign'">
            <div class="node-settings__ac-fields">
              <label for="ac-tag" class="node-settings__label">Tag</label>
              <input
                id="ac-tag"
                v-model="localData.config.tag"
                type="text"
                class="node-settings__input"
                placeholder="bijv. deal-gewonnen (leeg = geen tag)"
                @input="emitUpdate"
              />
              <p class="node-settings__hint">Leeg laten = geen tag toevoegen aan het contact.</p>

              <label class="node-settings__label">Contactvelden bijwerken (optioneel)</label>

              <!-- Template variabelen info -->
              <div class="node-settings__vars-info">
                <BaseIcon name="info-circle" size="13" class="node-settings__vars-info-icon" />
                <span>
                  Gebruik <code>&#123;&#123;variabele&#125;&#125;</code> voor dynamische waarden.
                  Klik op <strong>{ }</strong> naast een veld om een variabele in te voegen.
                </span>
              </div>

              <div
                v-for="(row, index) in (localData.config.contactFields || [])"
                :key="'ac-cf-' + index"
                class="node-settings__contact-row"
              >
                <select
                  v-model="row.acFieldId"
                  class="node-settings__select"
                  @change="emitUpdate"
                >
                  <option value="">AC veld kiezen...</option>
                  <option
                    v-for="f in acContactFields"
                    :key="f.id"
                    :value="f.id"
                  >
                    {{ f.title }}
                  </option>
                </select>
                <div class="node-settings__value-wrap">
                  <input
                    :ref="el => { if (el) valueInputRefs['contactFields-' + index] = el; }"
                    v-model="row.value"
                    type="text"
                    class="node-settings__input node-settings__input--value"
                    placeholder="Waarde of {{variabele}}"
                    @input="emitUpdate"
                  />
                  <div
                    class="node-settings__var-picker"
                    :ref="el => { if (el) varPickerRefs['contactFields-' + index] = el; }"
                  >
                    <button
                      type="button"
                      class="node-settings__var-btn"
                      :title="'Variabele invoegen'"
                      @click="openVarPicker('contactFields-' + index)"
                    >{ }</button>
                    <div
                      class="node-settings__var-dropdown"
                      v-show="activeVarPicker === 'contactFields-' + index"
                    >
                      <template v-for="group in varGroups" :key="group.group">
                        <div
                          class="node-settings__var-group-label"
                          :class="{ 'node-settings__var-group-label--dimmed': group.group === 'Offerte' && !varAvailability.proposal }"
                        >
                          {{ group.group }}
                          <span
                            v-if="group.group === 'Offerte' && !varAvailability.proposal"
                            class="node-settings__var-unavailable-tag"
                          >niet beschikbaar bij deal_created</span>
                        </div>
                        <button
                          v-for="v in group.vars"
                          :key="v.key"
                          type="button"
                          class="node-settings__var-item"
                          :class="{ 'node-settings__var-item--dimmed': group.group === 'Offerte' && !varAvailability.proposal }"
                          @mousedown.prevent="insertVariable('contactFields', index, v.key)"
                        >
                          <code class="node-settings__var-code">&#123;&#123;{{ v.key }}&#125;&#125;</code>
                          <span class="node-settings__var-example">{{ v.example }}</span>
                        </button>
                      </template>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  class="node-settings__remove-btn"
                  @click="removeAcContactField(index)"
                  aria-label="Verwijderen"
                >
                  <BaseIcon name="trash" size="14" />
                </button>
              </div>
              <button
                type="button"
                class="node-settings__add-btn"
                @click="addAcContactField"
              >
                <BaseIcon name="plus" size="14" />
                Veld toevoegen
              </button>
            </div>
          </template>

          <!-- Loops: event name + event properties + mailing lists -->
          <template v-if="selectedIntegration?.type === 'loops'">
            <div class="node-settings__ac-fields">
              <label for="loops-event-name" class="node-settings__label">Event naam (optioneel)</label>
              <input
                id="loops-event-name"
                v-model="localData.config.eventName"
                type="text"
                class="node-settings__input"
                placeholder="bijv. dealWon"
                @input="emitUpdate"
              />
              <p class="node-settings__hint">
                Leeg laten als je alleen een mailing list wilt bijwerken. Mag geen dubbele
                punt (<code>:</code>) bevatten.
              </p>

              <label class="node-settings__label">Event properties (optioneel)</label>

              <div class="node-settings__vars-info">
                <BaseIcon name="info-circle" size="13" class="node-settings__vars-info-icon" />
                <span>
                  Gebruik <code>&#123;&#123;variabele&#125;&#125;</code> voor dynamische waarden.
                  Klik op <strong>{ }</strong> naast een veld om een variabele in te voegen.
                </span>
              </div>

              <div
                v-for="(row, index) in (localData.config.eventProperties || [])"
                :key="'loops-ep-' + index"
                class="node-settings__contact-row"
              >
                <input
                  v-model="row.key"
                  type="text"
                  class="node-settings__input node-settings__input--key"
                  placeholder="property naam"
                  @input="emitUpdate"
                />
                <div class="node-settings__value-wrap">
                  <input
                    :ref="el => { if (el) valueInputRefs['eventProperties-' + index] = el; }"
                    v-model="row.value"
                    type="text"
                    class="node-settings__input node-settings__input--value"
                    placeholder="Waarde of {{variabele}}"
                    @input="emitUpdate"
                  />
                  <div
                    class="node-settings__var-picker"
                    :ref="el => { if (el) varPickerRefs['eventProperties-' + index] = el; }"
                  >
                    <button
                      type="button"
                      class="node-settings__var-btn"
                      :title="'Variabele invoegen'"
                      @click="openVarPicker('eventProperties-' + index)"
                    >{ }</button>
                    <div
                      class="node-settings__var-dropdown"
                      v-show="activeVarPicker === 'eventProperties-' + index"
                    >
                      <template v-for="group in varGroups" :key="group.group">
                        <div
                          class="node-settings__var-group-label"
                          :class="{ 'node-settings__var-group-label--dimmed': group.group === 'Offerte' && !varAvailability.proposal }"
                        >
                          {{ group.group }}
                          <span
                            v-if="group.group === 'Offerte' && !varAvailability.proposal"
                            class="node-settings__var-unavailable-tag"
                          >niet beschikbaar bij deal_created</span>
                        </div>
                        <button
                          v-for="v in group.vars"
                          :key="v.key"
                          type="button"
                          class="node-settings__var-item"
                          :class="{ 'node-settings__var-item--dimmed': group.group === 'Offerte' && !varAvailability.proposal }"
                          @mousedown.prevent="insertVariable('eventProperties', index, v.key)"
                        >
                          <code class="node-settings__var-code">&#123;&#123;{{ v.key }}&#125;&#125;</code>
                          <span class="node-settings__var-example">{{ v.example }}</span>
                        </button>
                      </template>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  class="node-settings__remove-btn"
                  @click="removeLoopsEventProperty(index)"
                  aria-label="Verwijderen"
                >
                  <BaseIcon name="trash" size="14" />
                </button>
              </div>
              <button
                type="button"
                class="node-settings__add-btn"
                @click="addLoopsEventProperty"
              >
                <BaseIcon name="plus" size="14" />
                Property toevoegen
              </button>

              <label class="node-settings__label">Mailing lists (optioneel)</label>
              <p class="node-settings__hint">
                Voeg het contact toe aan een list of verwijder het eruit. Vul minstens een
                eventnaam of een list-actie in.
              </p>

              <div
                v-if="loopsListsLoading"
                class="node-settings__hint"
              >
                Lists laden…
              </div>
              <div
                v-else-if="loopsListsError"
                class="alert alert--warning"
              >
                {{ loopsListsError }}
              </div>

              <div
                v-for="(row, index) in (localData.config.mailingLists || [])"
                :key="'loops-ml-' + index"
                class="node-settings__contact-row"
              >
                <select
                  v-model="row.listId"
                  class="node-settings__select node-settings__select--grow"
                  @change="emitUpdate"
                >
                  <option value="">List kiezen...</option>
                  <option
                    v-for="list in loopsLists"
                    :key="list.id"
                    :value="list.id"
                  >
                    {{ list.name }}
                  </option>
                </select>
                <select
                  v-model="row.action"
                  class="node-settings__select node-settings__select--action"
                  @change="emitUpdate"
                >
                  <option value="add">Toevoegen</option>
                  <option value="remove">Verwijderen</option>
                </select>
                <button
                  type="button"
                  class="node-settings__remove-btn"
                  @click="removeLoopsMailingList(index)"
                  aria-label="Verwijderen"
                >
                  <BaseIcon name="trash" size="14" />
                </button>
              </div>
              <button
                type="button"
                class="node-settings__add-btn"
                :disabled="loopsListsLoading || !!loopsListsError"
                @click="addLoopsMailingList"
              >
                <BaseIcon name="plus" size="14" />
                List-actie toevoegen
              </button>
            </div>
          </template>
        </template>
        <template v-else>
          <div class="alert alert--info">
            No integrations found. First add your integrations at the
            <router-link :to="{ path: '/settings/integrations' }">integratiespagina</router-link>
            .
          </div>
        </template>
      </template>

      <ConditionConfig
        v-else-if="localData.type === 'condition'"
        v-model="localData.config"
        :currentAutomation="currentAutomation"
      />

      <TaskConfig
        v-else-if="localData.type === 'task'"
        :modelValue="localData.config"
        @update:config="handleConfigUpdate"
      />

      <DelayConfig
        v-else-if="localData.type === 'delay'"
        :model-value="localData.config"
        @update:model-value="handleConfigUpdate"
      />

      <template v-else-if="localData.type === 'assign_user'">
        <template v-if="!isDealTrigger">
          <div class="alert alert--warning">
            Deze actie werkt alleen bij deal-triggers
            (<code>deal_created</code> of <code>deal_status_change</code>).
            Wissel van trigger of verwijder deze stap.
          </div>
        </template>
        <AssignUserConfig
          v-else
          :model-value="localData.config"
          @update:model-value="handleConfigUpdate"
        />
      </template>

      <template v-else-if="localData.type === 'create_deal_task'">
        <template v-if="!isDealTrigger">
          <div class="alert alert--warning">
            Deze actie werkt alleen bij deal-triggers
            (<code>deal_created</code> of <code>deal_status_change</code>).
            Wissel van trigger of verwijder deze stap.
          </div>
        </template>
        <DealTaskConfig
          v-else
          :model-value="localData.config"
          @update:model-value="handleConfigUpdate"
        />
      </template>

      <template v-else-if="localData.type === 'move_deal_stage'">
        <template v-if="!isDealTrigger">
          <div class="alert alert--warning">
            Deze actie werkt alleen bij deal-triggers
            (<code>deal_created</code> of <code>deal_status_change</code>).
            Wissel van trigger of verwijder deze stap.
          </div>
        </template>
        <template v-else>
          <SelectInput
            id="move-deal-pipeline"
            label="Pipeline"
            v-model="localData.config.pipelineId"
            :options="movePipelineOptions"
            placeholder="Kies een pipeline"
            @update:modelValue="handleMoveStagePipelineChange"
          />
          <SelectInput
            id="move-deal-to-stage"
            label="Doel-stage"
            v-model="localData.config.toStageId"
            :options="moveToStageOptions"
            :disabled="!localData.config.pipelineId"
            :placeholder="
              localData.config.pipelineId
                ? 'Kies een stage'
                : 'Kies eerst een pipeline'
            "
            @update:modelValue="emitUpdate"
          />
          <div class="node-settings__info-tooltip">
            <BaseIcon name="info-circle" size="13" class="node-settings__info-icon" />
            <span>
              Deze verplaatsing triggert geen nieuwe
              <code>deal_status_change</code>-automations
              (loop-preventie).
            </span>
          </div>
        </template>
      </template>
    </template>
  </div>
</template>

<script>
  import { ref, watch, computed, onMounted, onBeforeUnmount } from 'vue';
  import axios from 'axios';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import ConditionConfig from './configs/ConditionConfig.vue';
  import TaskConfig from './configs/TaskConfig.vue';
  import DelayConfig from './configs/DelayConfig.vue';
  import AssignUserConfig from './configs/AssignUserConfig.vue';
  import DealTaskConfig from './configs/DealTaskConfig.vue';
  import { useFormStore } from '@/store/formStore';
  import { useDealStore } from '@/store/dealStore';
  import { useIntegrationStore } from '@/store/integrationStore';
  import { dealService } from '@/services/dealService';
  import {
    TRIGGER_TYPES,
    DEAL_TRIGGER_TYPES,
    ACTION_TYPES,
    TEMPLATE_VARIABLES,
    PROPOSAL_TYPE_OPTIONS,
    PROJECT_STATUS_OPTIONS,
    PROPOSAL_STATUS_OPTIONS,
    WORK_ORDER_TYPE_OPTIONS,
    WORK_ORDER_TRIGGER_TYPES,
  } from '@/constants/automations';
  import { INTEGRATION_TYPES } from '@/constants/integrationTypes';
  import { DEAL_STATUSES } from '@/constants/statuses';

  // Form-submission fields are inserted as {{field.<element_id>}}, where
  // element_id is the stable element.id from form.pages[].elements[]. The label
  // is only used as display text in the var-picker. The backend resolves
  // field.<element_id> against a fieldId → formattedValue map.
  const FORM_FIELD_PREFIX = 'field.';
  const FORM_FIELD_GROUP_LABEL = 'Formulier';

  // Display-only form elements that never produce submission data and so are
  // not useful as variables in the var-picker.
  const NON_INPUT_FORM_ELEMENTS = new Set([
    'heading',
    'text',
    'html',
    'notice',
    'image',
    'button',
    'thank-you',
    'product-advise',
    'submission-data',
  ]);

  export default {
    name: 'NodeSettings',

    components: {
      SelectInput,
      BaseIcon,
      ConditionConfig,
      TaskConfig,
      DelayConfig,
      AssignUserConfig,
      DealTaskConfig,
    },

    props: {
      type: {
        type: String,
        required: true,
        validator: (value) => ['trigger', 'action', 'condition'].includes(value),
      },
      nodeData: {
        type: Object,
        required: true,
      },
      hasConnections: {
        type: Boolean,
        default: false,
      },
      currentAutomation: {
        type: Object,
        default: () => ({}),
      },
    },

    emits: ['update'],

    setup(props, { emit }) {
      const formStore = useFormStore();
      const dealStore = useDealStore();
      const integrationStore = useIntegrationStore();
      const cloneNodeData = (data) => JSON.parse(JSON.stringify(data || {}));
      const localData = ref(cloneNodeData(props.nodeData));
      const stages = ref([]);
      const availableIntegrations = ref([]);
      const acContactFields = ref([]);
      const loopsLists = ref([]);
      const loopsListsLoading = ref(false);
      const loopsListsError = ref('');

      // The variable picker is shared by the ActiveCampaign contact-field rows
      // and the Loops event-property rows. We key everything by a composite
      // string ("<targetKey>-<index>") so the two lists never collide.
      const activeVarPicker = ref(null);
      const valueInputRefs = ref({});
      const varPickerRefs = ref({});

      const currentTriggerType = computed(
        () => props.currentAutomation?.trigger_type || null
      );

      const varAvailability = computed(() => {
        const t = currentTriggerType.value;
        return {
          proposal: t !== 'deal_created',
        };
      });

      // For a form_submission trigger we expose the form's own fields in the
      // var-picker so values can be mapped to e.g. a Loops event property.
      // The full pages/elements blob is fetched on demand (cached in formStore).
      const currentFormId = computed(() => {
        if (currentTriggerType.value !== 'form_submission') return null;
        const details = props.currentAutomation?.trigger_details || {};
        return details.formId || props.currentAutomation?.form_id || null;
      });

      const currentForm = ref(null);

      watch(
        currentFormId,
        async (formId) => {
          if (!formId) {
            currentForm.value = null;
            return;
          }
          const cached = formStore.formDetailsCache[formId];
          if (cached) currentForm.value = cached;
          try {
            const form = await formStore.ensureFormDetails(formId);
            if (formId === currentFormId.value) {
              currentForm.value = form || null;
            }
          } catch (error) {
            console.warn('Formulierdetails ophalen mislukt:', error);
            if (formId === currentFormId.value) currentForm.value = null;
          }
        },
        { immediate: true }
      );

      const formFieldVars = computed(() => {
        const form = currentForm.value;
        if (!Array.isArray(form?.pages)) return [];
        const vars = [];
        const addElement = (el) => {
          if (!el?.id) return;
          if (NON_INPUT_FORM_ELEMENTS.has(el.type)) return;
          const label = el.props?.label || el.props?.placeholder || el.id;
          vars.push({ key: `${FORM_FIELD_PREFIX}${el.id}`, example: label });
        };
        form.pages.forEach((page) => {
          // Forms store elements directly on the page (page.elements). Some
          // legacy/alternative shapes nest them under page.blocks[].elements.
          (page.elements || []).forEach(addElement);
          (page.blocks || []).forEach((block) =>
            (block.elements || []).forEach(addElement)
          );
        });
        return vars;
      });

      // Combined groups shown in the var-picker: the static template variables
      // plus a dynamic "Formulier" group when a form_submission trigger is set.
      const varGroups = computed(() => {
        if (!formFieldVars.value.length) return TEMPLATE_VARIABLES;
        return [
          ...TEMPLATE_VARIABLES,
          { group: FORM_FIELD_GROUP_LABEL, vars: formFieldVars.value },
        ];
      });

      const openVarPicker = (pickerKey) => {
        activeVarPicker.value = activeVarPicker.value === pickerKey ? null : pickerKey;
      };

      const insertVariable = (targetKey, index, key) => {
        const row = localData.value.config?.[targetKey]?.[index];
        if (!row) return;
        row.value = (row.value || '') + `{{${key}}}`;
        activeVarPicker.value = null;
        emitUpdate();
      };

      const handleOutsideClick = (e) => {
        if (activeVarPicker.value === null) return;
        const el = varPickerRefs.value[activeVarPicker.value];
        if (el && !el.contains(e.target)) {
          activeVarPicker.value = null;
        }
      };

      onBeforeUnmount(() => {
        document.removeEventListener('click', handleOutsideClick);
      });

      const forms = computed(() => formStore.forms);
      const formOptions = computed(() =>
        forms.value.map((form) => ({
          value: form.id,
          label: form.name,
        }))
      );

      const pipelineOptions = computed(() => [
        { value: null, label: 'All pipelines' },
        ...dealStore.pipelines.map((p) => ({ value: p.id, label: p.name })),
      ]);

      const stageOptions = computed(() => [
        { value: '', label: 'Any stage' },
        ...stages.value.map((s) => ({ value: s.id, label: s.name })),
      ]);

      // Separate option lists for the move_deal_stage action: pipeline is
      // required (no "All pipelines" entry) and stage is required
      // (no "Any stage" entry).
      const movePipelineOptions = computed(() =>
        dealStore.pipelines.map((p) => ({ value: p.id, label: p.name }))
      );

      const moveToStageOptions = computed(() =>
        stages.value.map((s) => ({ value: s.id, label: s.name }))
      );

      const isDealTrigger = computed(() =>
        DEAL_TRIGGER_TYPES.includes(currentTriggerType.value)
      );

      const dealStatusTypeOptions = computed(() => [
        { value: null, label: 'All status changes' },
        { value: 'any', label: 'Stage move' },
        { value: 'won', label: 'Won' },
        { value: 'lost', label: 'Lost' },
        { value: 'reopened', label: 'Reopened' },
      ]);

      const selectedIntegration = computed(() =>
        integrationStore.integrations.find(
          (i) => i.id === localData.value.config?.integrationId
        )
      );

      const selectedIntegrationDescription = computed(() => {
        const i = selectedIntegration.value;
        if (!i) return '';
        const typeName = INTEGRATION_TYPES[i.type]?.name || i.name || i.type;
        return `${typeName} wordt uitgevoerd met de instellingen die je bij de integratie hebt geconfigureerd (formulieren, veldkoppelingen, enz.).`;
      });

      const fetchAcMetadata = async (integrationId) => {
        if (!integrationId) {
          acContactFields.value = [];
          return;
        }
        try {
          const { data } = await axios.get(
            `/integrations/activecampaign/${integrationId}/metadata`
          );
          acContactFields.value = data?.contactFields || [];
          if (!Array.isArray(localData.value.config.contactFields)) {
            localData.value.config.contactFields = [];
          }
          if (typeof localData.value.config.tag !== 'string') {
            localData.value.config.tag = '';
          }
          emitUpdate();
        } catch (e) {
          console.warn('ActiveCampaign metadata ophalen mislukt:', e);
          acContactFields.value = [];
        }
      };

      const addAcContactField = () => {
        if (!localData.value.config.contactFields) {
          localData.value.config.contactFields = [];
        }
        localData.value.config.contactFields.push({ acFieldId: '', value: '' });
        emitUpdate();
      };

      const removeAcContactField = (index) => {
        localData.value.config.contactFields?.splice(index, 1);
        emitUpdate();
      };

      const addLoopsEventProperty = () => {
        if (!Array.isArray(localData.value.config.eventProperties)) {
          localData.value.config.eventProperties = [];
        }
        localData.value.config.eventProperties.push({ key: '', value: '' });
        emitUpdate();
      };

      const removeLoopsEventProperty = (index) => {
        localData.value.config.eventProperties?.splice(index, 1);
        emitUpdate();
      };

      const fetchLoopsLists = async (integrationId) => {
        if (!integrationId) {
          loopsLists.value = [];
          loopsListsError.value = '';
          return;
        }
        loopsListsLoading.value = true;
        loopsListsError.value = '';
        try {
          const { data } = await axios.get(`/integrations/loops/${integrationId}/lists`);
          loopsLists.value = data?.lists || [];
          if (!Array.isArray(localData.value.config.mailingLists)) {
            localData.value.config.mailingLists = [];
          }
          if (typeof localData.value.config.eventName !== 'string') {
            localData.value.config.eventName = '';
          }
          if (!Array.isArray(localData.value.config.eventProperties)) {
            localData.value.config.eventProperties = [];
          }
        } catch (e) {
          console.warn('Loops lists ophalen mislukt:', e);
          loopsLists.value = [];
          loopsListsError.value =
            e?.response?.data?.error || 'Mailing lists ophalen mislukt. Controleer de API key.';
        } finally {
          loopsListsLoading.value = false;
        }
      };

      const addLoopsMailingList = () => {
        if (!Array.isArray(localData.value.config.mailingLists)) {
          localData.value.config.mailingLists = [];
        }
        localData.value.config.mailingLists.push({ listId: '', action: 'add' });
        emitUpdate();
      };

      const removeLoopsMailingList = (index) => {
        localData.value.config.mailingLists?.splice(index, 1);
        emitUpdate();
      };

      const loadStages = async (pipelineId) => {
        if (!pipelineId) {
          stages.value = [];
          return;
        }
        try {
          const { stages: pipelineStages } = await dealService.getPipelineStages(pipelineId);
          stages.value = pipelineStages || [];
        } catch {
          stages.value = [];
        }
      };

      const handlePipelineChange = () => {
        localData.value.config.fromStageId = '';
        localData.value.config.toStageId = '';
        loadStages(localData.value.config.pipelineId);
        emitUpdate();
      };

      const handleMoveStagePipelineChange = () => {
        localData.value.config.toStageId = '';
        loadStages(localData.value.config.pipelineId);
        emitUpdate();
      };

      onMounted(async () => {
        document.addEventListener('click', handleOutsideClick);
        if (!dealStore.pipelines.length) {
          await dealStore.fetchPipelines();
        }
        if (localData.value.config?.pipelineId) {
          loadStages(localData.value.config.pipelineId);
        }
        if (!integrationStore.integrations.length) {
          await integrationStore.fetchIntegrations();
        }
        availableIntegrations.value = integrationStore.integrations.filter(
          (i) => i.is_active && INTEGRATION_TYPES[i.type]?.canUseAutomation
        );
        const integrationId = localData.value.config?.integrationId;
        const integration = integrationStore.integrations.find((i) => i.id === integrationId);
        if (integration?.type === 'activecampaign' && integrationId) {
          await fetchAcMetadata(integrationId);
        } else if (integration?.type === 'loops' && integrationId) {
          await fetchLoopsLists(integrationId);
        }
      });

      watch(
        () => localData.value.config?.integrationId,
        (integrationId) => {
          const integration = integrationStore.integrations.find((i) => i.id === integrationId);
          if (integration?.type === 'activecampaign') {
            fetchAcMetadata(integrationId);
            if (!Array.isArray(localData.value.config.contactFields)) {
              localData.value.config.contactFields = [];
            }
            if (typeof localData.value.config.tag !== 'string') {
              localData.value.config.tag = '';
            }
            loopsLists.value = [];
            loopsListsError.value = '';
          } else if (integration?.type === 'loops') {
            acContactFields.value = [];
            if (typeof localData.value.config.eventName !== 'string') {
              localData.value.config.eventName = '';
            }
            if (!Array.isArray(localData.value.config.eventProperties)) {
              localData.value.config.eventProperties = [];
            }
            if (!Array.isArray(localData.value.config.mailingLists)) {
              localData.value.config.mailingLists = [];
            }
            fetchLoopsLists(integrationId);
          } else {
            acContactFields.value = [];
            loopsLists.value = [];
            loopsListsError.value = '';
          }
        }
      );

      const handleTypeChange = () => {
        localData.value.config = getDefaultConfig(localData.value.type);
        emitUpdate();
      };

      const getDefaultConfig = (type) => {
        switch (type) {
          case 'form_submission':
            return { formId: null };
          case 'status_change':
            return { fromStatus: null, toStatus: null };
          case 'deal_created':
            return { pipelineId: null, source: 'any' };
          case 'deal_status_change':
            return {
              pipelineId: null,
              fromStageId: '',
              toStageId: '',
              statusType: null,
            };
          case 'proposal_accepted':
          case 'proposal_rejected':
            return { proposalType: 'any' };
          case 'proposal_status_change':
            return { fromStatus: 'any', toStatus: 'any', proposalType: 'any' };
          case 'project_status_change':
            return { fromStatus: 'any', toStatus: 'any' };
          case 'project_fully_completed':
            return { fromStatus: 'any', toStatus: 'completed' };
          case 'work_order_completed':
          case 'work_visit_confirmed':
          case 'work_visit_completed':
          case 'project_all_work_orders_completed':
            return { workOrderType: 'any' };
          case 'scheduled_event':
            return {
              frequency: 'daily',
              schedule_settings: { time: '09:00' },
            };
          case 'condition':
            return { conditions: [] };
          case 'delay':
            return { duration: 1, unit: 'hours' };
          case 'integration':
            return {
              integrationId: null,
              tag: '',
              contactFields: [],
              eventName: '',
              eventProperties: [],
              mailingLists: [],
            };
          case 'task':
            return { taskType: null };
        case 'move_deal_stage':
          return { pipelineId: null, toStageId: '' };
        case 'assign_user':
          return { assignmentMode: 'specific', userIds: [] };
        case 'create_deal_task':
          return {
            title: '',
            description: '',
            taskType: 'general',
            priority: 'medium',
            dueInDays: 0,
            assignTo: 'deal_owner',
          };
        default:
          return {};
        }
      };

      const handleConfigUpdate = (newConfig) => {
        if (JSON.stringify(localData.value.config) === JSON.stringify(newConfig)) {
          return;
        }
        localData.value.config = newConfig;
        emitUpdate();
      };

      const emitUpdate = () => {
        emit('update', localData.value);
      };

      watch(
        () => props.nodeData,
        (newVal) => {
          if (JSON.stringify(localData.value) !== JSON.stringify(newVal)) {
            localData.value = cloneNodeData(newVal);
          }
        },
        { deep: true }
      );

      return {
        localData,
        forms,
        formOptions,
        pipelineOptions,
        stageOptions,
        movePipelineOptions,
        moveToStageOptions,
        isDealTrigger,
        dealStatusTypeOptions,
        availableIntegrations,
        selectedIntegration,
        selectedIntegrationDescription,
        acContactFields,
        addAcContactField,
        removeAcContactField,
        addLoopsEventProperty,
        removeLoopsEventProperty,
        loopsLists,
        loopsListsLoading,
        loopsListsError,
        addLoopsMailingList,
        removeLoopsMailingList,
        handlePipelineChange,
        handleMoveStagePipelineChange,
        handleTypeChange,
        emitUpdate,
        triggerTypes: TRIGGER_TYPES,
        actionTypes: ACTION_TYPES,
        proposalTypeOptions: PROPOSAL_TYPE_OPTIONS,
        projectStatusOptions: PROJECT_STATUS_OPTIONS,
        proposalStatusOptions: PROPOSAL_STATUS_OPTIONS,
        workOrderTypeOptions: WORK_ORDER_TYPE_OPTIONS,
        WORK_ORDER_TRIGGER_TYPES,
        dealStatuses: DEAL_STATUSES,
        handleConfigUpdate,
        varGroups,
        activeVarPicker,
        varAvailability,
        valueInputRefs,
        varPickerRefs,
        openVarPicker,
        insertVariable,
      };
    },
  };
</script>

<style lang="scss" scoped>
  .node-settings {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    .alert {
      margin-top: 1rem;
      padding: 0.75rem;
      border-radius: 6px;
      font-size: 0.875rem;

      &--info {
        background: var(--blue-50);
        border: 1px solid var(--blue-200);
        color: var(--blue-700);
      }

      &--warning {
        background: var(--amber-50);
        border: 1px solid var(--amber-200);
        color: var(--amber-700);

        code {
          background: var(--amber-100);
          padding: 0 3px;
          border-radius: 3px;
          font-family: monospace;
          font-size: 0.75rem;
        }
      }

      a {
        color: var(--blue-600);
        text-decoration: underline;
      }
    }

    &__info-tooltip {
      display: flex;
      align-items: flex-start;
      gap: 0.4rem;
      padding: 0.5rem 0.6rem;
      background: var(--slate-50);
      border: 1px solid var(--slate-200);
      border-radius: 6px;
      font-size: 0.75rem;
      color: var(--slate-600);
      line-height: 1.4;
      margin-top: 0.25rem;

      code {
        background: var(--slate-100);
        padding: 0 3px;
        border-radius: 3px;
        font-family: monospace;
        font-size: 0.7rem;
      }
    }

    &__info-icon {
      flex-shrink: 0;
      margin-top: 1px;
      color: var(--slate-500);
    }

    &__integration-info {
      margin-top: 0.75rem;
      padding: 0.75rem;
      background: var(--slate-50);
      border: 1px solid var(--slate-200);
      border-radius: 6px;
      font-size: 0.875rem;
    }

    &__integration-desc {
      margin: 0 0 0.5rem 0;
      color: var(--slate-600);
      line-height: 1.4;
    }

    &__integration-link {
      color: var(--primary-600);
      text-decoration: none;
      font-weight: 500;

      &:hover {
        text-decoration: underline;
      }
    }

    &__ac-fields {
      margin-top: 1rem;
      padding-top: 1rem;
      border-top: 1px solid var(--slate-200);
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    &__label {
      display: block;
      font-size: 0.875rem;
      font-weight: 500;
      color: var(--slate-700);
      margin-bottom: 0.25rem;
    }

    &__input {
      width: 100%;
      padding: 0.5rem 0.75rem;
      border: 1px solid var(--slate-300);
      border-radius: 6px;
      font-size: 0.875rem;

      &--value {
        flex: 1;
        min-width: 0;
        border-radius: 6px 0 0 6px;
        border-right: none;
      }

      &--key {
        width: 130px;
        flex-shrink: 0;
      }
    }

    &__hint {
      margin: 0 0 0.5rem 0;
      font-size: 0.75rem;
      color: var(--slate-500);
    }

    &__select {
      padding: 0.5rem 0.75rem;
      border: 1px solid var(--slate-300);
      border-radius: 6px;
      font-size: 0.875rem;
      min-width: 140px;
      background: white;

      &--action {
        min-width: 120px;
        flex-shrink: 0;
      }

      &--grow {
        flex: 1;
        min-width: 0;
      }
    }

    &__vars-info {
      display: flex;
      align-items: flex-start;
      gap: 0.4rem;
      padding: 0.5rem 0.6rem;
      background: var(--brand-50);
      border: 1px solid var(--brand-200);
      border-radius: 6px;
      font-size: 0.75rem;
      color: var(--brand-700);
      line-height: 1.4;

      code {
        font-family: monospace;
        background: var(--brand-100);
        padding: 0 3px;
        border-radius: 3px;
        font-size: 0.7rem;
      }
    }

    &__vars-info-icon {
      flex-shrink: 0;
      margin-top: 1px;
      color: var(--brand-500);
    }

    &__contact-row {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    &__value-wrap {
      flex: 1;
      min-width: 0;
      position: relative;
      display: flex;
      align-items: center;
    }

    &__var-picker {
      position: relative;
      flex-shrink: 0;
      align-self: stretch;
      display: flex;
    }

    &__var-btn {
      padding: 0 0.6rem;
      border: 1px solid var(--slate-300);
      border-radius: 0 6px 6px 0;
      background: var(--slate-50);
      font-size: 0.7rem;
      font-weight: 600;
      color: var(--slate-500);
      cursor: pointer;
      white-space: nowrap;
      font-family: monospace;
      width: 100%;

      &:hover {
        border-color: var(--brand-400);
        color: var(--brand-600);
        background: var(--brand-50);
      }
    }

    &__var-dropdown {
      position: absolute;
      right: 0;
      top: calc(100% + 4px);
      z-index: 999;
      background: white;
      border: 1px solid var(--slate-200);
      border-radius: 8px;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
      width: 260px;
      max-height: 320px;
      overflow-y: auto;
      padding: 0.25rem 0;
    }

    &__var-group-label {
      padding: 0.4rem 0.75rem 0.2rem;
      font-size: 0.65rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--slate-400);
      display: flex;
      align-items: center;
      gap: 0.4rem;
      flex-wrap: wrap;

      &--dimmed {
        color: var(--slate-300);
      }
    }

    &__var-unavailable-tag {
      font-size: 0.6rem;
      font-weight: 500;
      text-transform: none;
      letter-spacing: 0;
      color: var(--amber-600);
      background: var(--amber-50);
      border: 1px solid var(--amber-200);
      border-radius: 4px;
      padding: 0 4px;
    }

    &__var-item {
      display: flex;
      align-items: baseline;
      gap: 0.5rem;
      width: 100%;
      padding: 0.35rem 0.75rem;
      background: none;
      border: none;
      cursor: pointer;
      text-align: left;
      font-size: 0.8rem;

      &:hover {
        background: var(--slate-50);
      }

      &--dimmed {
        opacity: 0.4;
        cursor: default;

        &:hover {
          background: none;
        }
      }
    }

    &__var-code {
      font-family: monospace;
      font-size: 0.75rem;
      color: var(--brand-700);
      background: var(--brand-50);
      padding: 1px 4px;
      border-radius: 3px;
      flex-shrink: 0;
    }

    &__var-example {
      font-size: 0.7rem;
      color: var(--slate-400);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__remove-btn {
      padding: 0.5rem;
      border: 1px solid var(--slate-300);
      border-radius: 6px;
      background: white;
      color: var(--slate-600);
      cursor: pointer;
      flex-shrink: 0;

      &:hover {
        background: var(--slate-50);
        color: var(--red-600);
      }
    }

    &__add-btn {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 0.75rem;
      border: 1px dashed var(--slate-300);
      border-radius: 6px;
      background: var(--slate-50);
      font-size: 0.875rem;
      color: var(--slate-600);
      cursor: pointer;
      margin-top: 0.25rem;

      &:hover {
        border-color: var(--primary-400);
        color: var(--primary-600);
        background: var(--primary-50);
      }
    }
  }
</style>
